import React, { useEffect, useState } from "react";
import { Box, Button, Typography, AlertTitle, Alert, Stack, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemAvatar, ListItemText, Avatar, IconButton } from "@mui/material";
import api from '../../apis/api';
import { Tooltip } from '@mui/material';
import { FileUploader } from "react-drag-drop-files";
import { uploadProjectVideoFile, uploadProjectVerticalVideoFile } from "../../apis/apiList/projectApi";
import { useMessage } from "../../context/MessageContext";
import { useApp } from "../../context/AppContext";
import DownloadVimeoVideo from "./DownloadVimeoVideo";
// import TemplateSelector from './TemplateSelector';
// import DynamicPlaceholderForm from './DynamicPlaceholderForm';
import CreateVideoModal from './CreateVideoModal';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';

const fileTypes = [
  "mp4",
  "avi",
  "mov",
  "wmv",
  "flv",
  "mkv",
  "webm",
  "mpeg",
  "mpg",
  "3gp",
  "m4v",
];

const CreateVideoStage = ({ project, setProject, setApprovedVideoFileId, setApprovedVerticalVideoFileId, isFullAutomated }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [isSubmit, setIsSubmit] = useState(false);
  const [isSubmittingVerticalVideo, setIsSubmittingVerticalVideo] = useState(false);
  const { showMessage } = useMessage();
  const { userDetails } = useApp();
  const [projectDetail, setProjectDetail] = useState(project);
  const [showTemplateFlow, setShowTemplateFlow] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [uploadNull, setUploadNull] = useState(null);
  const [templateGenerating, setTemplateGenerating] = useState(false);
  const [templateStatusMessage, setTemplateStatusMessage] = useState('');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryVideos, setLibraryVideos] = useState([]);
  const [libraryError, setLibraryError] = useState(null);
  const [lastLibraryPayload, setLastLibraryPayload] = useState(null);
  const handleChange = (file) => {
    setFile(file);
    setUploadNull(file);
  };

  const pollRef = React.useRef(null);

  const fetchLibrary = async () => {
    if (!projectDetail?._id || !userDetails?.jwtToken) return;
    try {
      setLibraryLoading(true);
      setLibraryError(null);
      const res = await api.get(`/heygen/projects/${projectDetail._id}/video-versions`, {
        headers: { 'x-access-token': `${userDetails.jwtToken}` }
      });
      console.log('fetchLibrary response', res);
      const payload = res && res.data;
      console.log('fetchLibrary payload', payload);
      setLastLibraryPayload(payload);
      const versions = payload || [];
      console.log('fetchLibrary found versions count', (versions && versions.length) || 0);
      // Filter to only versions that were created via HeyGen template (have heygenTemplateId or templateSnapshot)
      const templateVersions = (versions || []).filter(v => v && (v.heygenTemplateId || v.templateSnapshot));
      setLibraryVideos(templateVersions);
      console.log('fetchLibrary templateVersions count', (templateVersions && templateVersions.length) || 0);
      // update status message to reflect only processing/rendering template version (do not show completed)
      if (templateVersions && templateVersions.length > 0) {
        const latest = templateVersions[0];
        const st = (latest && latest.status) ? String(latest.status).toLowerCase() : '';
        if (['processing','rendering','queued','in_progress','started'].includes(st)) {
          // setTemplateStatusMessage(`Template generation: ${latest.status}. After it completes it will be added to the library and you can regenerate it again.`);
          setTemplateStatusMessage(`Your video is currently being processed. Please check again later.`);
          setTemplateGenerating(true);
        } else {
          // hide completed/failed statuses from the main page status message
          setTemplateGenerating(false);
          setTemplateStatusMessage('');
        }
      }
    } catch (e) {
      console.error('Failed to fetch video versions', e);
      setLibraryError(e?.message || 'Failed to fetch');
    } finally {
      setLibraryLoading(false);
    }
  };

  useEffect(() => {
    setProjectDetail(project)
  }, [project]);

  useEffect(() => {
    fetchLibrary();
  }, [])

  const handleFileUpload = async () => {
    setIsSubmit(true);
    if (!file) {
      setError("Please upload file");
      setIsSubmit(false);
      return;
    }
    setError("");

    try {
      const formData = new FormData();

      formData.append("videoFile", file);

      const projectNew = await uploadProjectVideoFile(project._id, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-access-token": userDetails?.jwtToken
        },
      });
      
      let newProject = { ...projectDetail };
      newProject.vimeoVideoId =
        projectNew.data.vimeoVideoId;
      
      setApprovedVideoFileId(projectNew.data.vimeoVideoId)

      setProjectDetail(newProject);
      setProject(newProject);
      setFile(null)
      setIsSubmit(false);
      showMessage(projectNew.message, "success");
    } catch (err) {
      setIsSubmit(false);
      showMessage(err?.response?.data?.message, "error");
    }
  };
  
  const handleVerticalFileUpload = async () => {
    setIsSubmittingVerticalVideo(true);
    if (!file) {
      setError("Please upload vertical file");
      setIsSubmittingVerticalVideo(false);
      return;
    }
    setError("");

    try {
      const formData = new FormData();

      formData.append("videoFile", file);

      const projectNew = await uploadProjectVerticalVideoFile(project._id, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-access-token": userDetails?.jwtToken
        },
      });
      
      let newProject = { ...projectDetail };
      newProject.vimeoVerticalVideoId =
        projectNew.data.vimeoVerticalVideoId;

      setApprovedVerticalVideoFileId(projectNew.data.vimeoVerticalVideoId)
        
      setProjectDetail(newProject);
      setFile(null)
      setIsSubmittingVerticalVideo(false);
      showMessage(projectNew.message, "success");
    } catch (err) {
      setIsSubmittingVerticalVideo(false);
      showMessage(err?.response?.data?.message, "error");
    }
  };

  const boxCss = {
    m: 2,
    borderRadius: 2,
    backgroundColor: "#f9f9f9",
    border: "1px solid #dbdbdb",
  }
  const iframeCss = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    borderRadius: "10px 10px 0px 0px",
  }

  return (
    <>
      {userDetails && userDetails.role == "admin" && !isFullAutomated && (
        <Box className="fileUpload-stage">
          <FileUploader
            handleChange={handleChange}
            name="file"
            multiple={false}
            types={fileTypes}
            fileOrFiles={file}
            // label="Drag or click here to upload a video file"
            // hoverTitle="Drop the file here"
            uploadedLabel={`File selected: ${file?.name}.      Click Upload to start uploading.`}
          />
          {error && <Typography color="red">{error}</Typography>}

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap="20px">
              <Button
                variant="contained"
                sx={{ marginTop: "15px" }}
                disabled={isSubmit || projectDetail.approvals.video}
                onClick={handleFileUpload}
              >
                {" "}
                {isSubmit ? "Uploading..." : "Upload Horizontal Video"}
              </Button>

              <Button
                variant="contained"
                sx={{ marginTop: "15px" }}
                disabled={
                  // !projectDetail.approvals.video ||
                  isSubmittingVerticalVideo || projectDetail.approvals.video
                  // || projectDetail.vimeoVerticalVideoId
                }
                onClick={handleVerticalFileUpload}
              >
                {" "}
                {isSubmittingVerticalVideo ? "Uploading..." : "Upload Vertical Video"}
              </Button>
            </Stack>
          </Box>
        </Box>
      )}
       {templateStatusMessage && (
          <Box sx={{ mb: 2 }}>
            <Alert severity={templateStatusMessage.toLowerCase().includes('fail') ? 'error' : 'info'}>{templateStatusMessage}</Alert>
          </Box>
        )}
      {
        isFullAutomated ? (
          <>
            <Box mb={4}>
              {/* Header Section */}
              <Box 
                sx={{
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid #e0e0e0'
                }}
              >
                <Box>
                  <Typography 
                    variant="h2" 
                    sx={{
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #D10000 0%, #8B0000 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.5
                    }}
                  >
                    Auto-generated Video
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your latest horizontal video content
                  </Typography>
                </Box>
                {templateStatusMessage && (
                  <Box>
                    <Alert 
                      sx={{
                        p: '8px 16px',
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }} 
                      severity={templateStatusMessage.toLowerCase().includes('fail') ? 'error' : 'info'}
                    >
                      {templateStatusMessage}
                    </Alert>
                  </Box>
                )}
              </Box>

              {projectDetail?.vimeoVideoId ? (
                <Box>
                  {/* Main Video Section */}
                  <Box>
                    <Box 
                      sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 12px 48px rgba(0,0,0,0.18)',
                          transform: 'translateY(-4px)'
                        }
                      }}
                    >
                      <div style={{ padding: '56.25% 0 0 0', position: 'relative', width: '100%' }}>
                        <iframe
                          src={`https://player.vimeo.com/video/${projectDetail?.vimeoVideoId}`}
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                          style={{...iframeCss, borderRadius: 0}}
                        />
                      </div>
                    </Box>

                    <Box 
                      sx={{
                        display: 'flex', 
                        justifyContent: 'center',
                        gap: 2,
                        // mt: 3
                      }}
                    >
                      <DownloadVimeoVideo videoId={projectDetail?.vimeoVideoId} userDetails={userDetails} />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Alert 
                  severity="info"
                  sx={{
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    p: 2
                  }}
                >
                  <AlertTitle>No video available yet</AlertTitle>
                  The system will automatically generate your horizontal video. Please check back shortly.
                </Alert>
              )}
            </Box>

            {/* Previous Versions Section */}
            {projectDetail?.oldVimeoVideoIds && projectDetail.oldVimeoVideoIds.length > 0 && (
              <Box>
                <Box sx={{mb: 3}}>
                  <Typography 
                    variant="h3" 
                    sx={{
                      fontWeight: 600,
                      color: '#333',
                      mb: 0.5
                    }}
                  >
                    Previous Versions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Browse through earlier iterations of your video
                  </Typography>
                </Box>
                <Box 
                  sx={{
                    display: 'grid', 
                    gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)'}, 
                    gap: 3
                  }}
                >
                  {projectDetail.oldVimeoVideoIds.map((id, index) => (
                    <Box 
                      key={id} 
                      sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        background: '#fff',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                        border: '1px solid #e0e0e0',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          transform: 'translateY(-4px)',
                          borderColor: '#D10000'
                        }
                      }}
                    >
                      <div style={{ padding: '56.25% 0 0 0', position: 'relative', width: '100%' }}>
                        <iframe
                          src={`https://player.vimeo.com/video/${id}`}
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                          style={{...iframeCss, borderRadius: 0}}
                        />
                      </div>
                      <Box 
                        sx={{
                          // p: 2, 
                          // display: 'flex', 
                          // justifyContent: 'space-between', 
                          alignItems: 'center',
                          backgroundColor: '#f8f9fa'
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="h5" fontWeight={600} sx={{ p: '8px 0px 0px 10px' }}>
                            Version {projectDetail.oldVimeoVideoIds.length - index}
                          </Typography>
                          {/* <Typography variant="caption" color="text.secondary">
                            Previous iteration
                          </Typography> */}
                        </Box>
                        <DownloadVimeoVideo videoId={id} userDetails={userDetails} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )
        :
         <>   
          <Box mb='30px'>
            {projectDetail && (projectDetail?.vimeoVideoId || projectDetail?.vimeoVerticalVideoId)? (
              <Box>
                <Box sx={{ backgroundColor: "#ababab", p: 2, borderRadius: 2, mb: 2 }}>
                  <Typography variant='h2' >
                      Latest Video
                  </Typography>
                </Box>

                <Box sx={{ mt: '10px', width: '100%', display: {xs: 'block', md:'flex'}}}>
                  {projectDetail?.vimeoVideoId && <Box sx={{ width: {xs: '100%', md: '50%'}}}>
                    <Typography variant='h3' pl='16px'>
                      Horizontal Video
                    </Typography>
                    <Box
                      sx={boxCss}
                    >
                      {/* <Box mb={"30px"}>
                        <Typography variant={"h4"}>Uploaded Video:</Typography>
                      </Box> */}
                      <Box sx={{ width: "100%" }}>
                        {/* <iframe src="https://player.vimeo.com/video/1075222095" style="position:absolute;top:0;left:0;width:100%;height:100%;" >
                        </iframe> */}
                        <div style={{ padding: "56.25% 0 0 0", position: "relative", width: "100%" }}>
                          <iframe
                            src={`https://player.vimeo.com/video/${projectDetail?.vimeoVideoId}`}
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                            style={iframeCss}
                          />
                        </div>
                        {/* <video style={{width: '100%'}} controls>
                          <source src={projectDetail?.vimeoVideoId} type="video/mp4" />
                          Your browser does not support HTML video.
                        </video> */}
                      </Box>
                      <DownloadVimeoVideo videoId={projectDetail?.vimeoVideoId} userDetails={userDetails} />
                    </Box>
                  </Box>}
                  {
                    projectDetail?.vimeoVerticalVideoId && 
                    <Box sx={{ width: {xs: '100%', md: '50%'}}}>
                      <Typography variant='h3' pl='16px'>
                        Vertical video
                      </Typography>
                      <Box
                        sx={boxCss}
                      >
                        <Box sx={{ width: "100%", backgroundImage: "url(/images/background-blur.jpg)", backgroundSize: "cover", backgroundPosition: "center", borderRadius: "10px 10px 0px 0px",
                            }}>
                          <div style={{ padding: "56.25% 0 0 0", position: "relative" }}>
                            <iframe
                              src={`https://player.vimeo.com/video/${projectDetail?.vimeoVerticalVideoId}`}
                              frameBorder="0"
                              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                              style={iframeCss}
                            />
                          </div>
                        </Box>
                        
                        <DownloadVimeoVideo videoId={projectDetail?.vimeoVerticalVideoId} userDetails={userDetails} />
                      </Box>
                    </Box>
                  }
                </Box>
              </Box>
            ) : (
              <Box>
                {/* <Typography>Voice over not found</Typography> */}
                {userDetails && userDetails.role == "admin" ? (
                  <Alert severity="info">
                    No video uploaded. Drag and drop a file or click "Upload" to add
                    one.
                  </Alert>
                ) : (
                  <Alert severity="info">
                    No video uploaded yet! Waiting for the editor to add one.
                  </Alert>
                )}
              </Box>
            )}
          </Box>

          <Box>
            {projectDetail &&
              projectDetail?.oldVimeoVideoIds &&
              projectDetail?.oldVimeoVideoIds.length > 0 && (
              <>
                <Box sx={{ backgroundColor: "#ababab", p: 2, borderRadius: 2, mb: 2 }}>
                  <Typography variant='h2'>
                    Previous versions
                  </Typography>
                </Box>

                <Box sx={{ mt: '10px', width: '100%', display: {xs: 'block', md:'flex'}}}>
                  {
                    projectDetail?.oldVimeoVideoIds.length > 0 && (
                      <Box width={{xs: '100%', md: '50%'}}>
                        <Typography variant='h3' pl='16px'>
                          Previous Horizontal Videos
                        </Typography>
                        {projectDetail?.oldVimeoVideoIds.map((videoId)=>
                          <Box>
                            <Box
                              sx={boxCss}
                            >
                              <Box sx={{ width: "100%" }}>
                                <div style={{ padding: "56.25% 0 0 0", position: "relative" }}>
                                  <iframe
                                    src={`https://player.vimeo.com/video/${videoId}`}
                                    frameBorder="0"
                                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                                    style={iframeCss}
                                  />
                                </div>
                              </Box>
                              
                              <DownloadVimeoVideo videoId={videoId} userDetails={userDetails} />
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )
                  }
                  {
                    projectDetail?.oldVimeoVerticalVideoIds && projectDetail?.oldVimeoVerticalVideoIds.length > 0 &&
                    <Box width={{xs: '100%', md: '50%'}}>
                      <Typography variant='h3' pl='16px'>
                        Previous Vertical Videos
                      </Typography>
                      {projectDetail?.oldVimeoVerticalVideoIds.map((videoId)=>
                        <Box>
                          <Box
                            sx={boxCss}
                          >
                            <Box 
                              sx={{
                                width: "100%",
                                backgroundImage: "url(/images/background-blur.jpg)",
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                borderRadius: "10px",
                              }}
                            >
                              <div style={{ padding: "56.25% 0 0 0", position: "relative" }}>
                                <iframe
                                  src={`https://player.vimeo.com/video/${videoId}`}
                                  frameBorder="0"
                                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                                  style={iframeCss}
                                />
                              </div>
                            </Box>
                            
                            <DownloadVimeoVideo videoId={videoId} userDetails={userDetails} />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  }
                </Box>
              </> 
              )}
          </Box>
         </>
      }
    </>
  );
};

export default CreateVideoStage;
